import cloneDeep from 'lodash/cloneDeep';
import { useRef } from 'react';
import { trackConfig, useFramework, useIdentity } from '@/framework';
import type { Displacement } from '@/plugins/displacement';
import type { ITouchKitEvent } from '@/utils/event-kit';
import { convertLayerPointToOrigin, getCenterPointForOrigin } from '@/utils/layer-kit';
import { addPoint, rotatePointWithFocus } from '@/utils/math-kit';
import {
    BrushType,
    LayerType,
    ResizeOperateType,
    type IBrushLayerProps,
    type ILayerProps,
    type IPoint,
} from '@/version-types';
import { generateLinePath, getBrushBaseInfo, getPathOffset, LAYER_EDGE_GAP, updateScreenshotUrl } from '../utils';

export const useLineOperate = () => {
    const identity = useIdentity();
    const framework = useFramework();
    const oldInfo = useRef<ILayerProps>();
    const leftStartPoint = useRef<IPoint>();
    const rightStartPoint = useRef<IPoint>();
    const mouseStartPoint = useRef<IPoint>();

    return {
        resize(e: ITouchKitEvent, layer: ILayerProps, operateType: string) {
            const scale = framework.service.edgelessCanvas.zoom;

            if (e.isStart) {
                framework.service.editor.setIsBaseOperate(true);
                oldInfo.current = cloneDeep(layer);
                const centerPoint = getCenterPointForOrigin(layer);
                rightStartPoint.current = rotatePointWithFocus(
                    { x: layer.left + layer.width, y: centerPoint.y },
                    centerPoint,
                    layer.rotate,
                );
                leftStartPoint.current = rotatePointWithFocus(
                    { x: layer.left, y: centerPoint.y },
                    centerPoint,
                    layer.rotate,
                );
                mouseStartPoint.current = convertLayerPointToOrigin(e.currentPoint, scale);
            }

            if (!rightStartPoint.current || !leftStartPoint.current || !mouseStartPoint.current || !oldInfo.current)
                return;

            const mouseCurPoint = convertLayerPointToOrigin(e.currentPoint, scale);
            const offsetPoint = {
                x: ~~(mouseCurPoint.x - mouseStartPoint.current.x),
                y: ~~(mouseCurPoint.y - mouseStartPoint.current.y),
            };
            const leftCurPoint = addPoint(offsetPoint, leftStartPoint.current);
            const rightCurPoint = addPoint(offsetPoint, rightStartPoint.current);
            const startPoint = operateType === ResizeOperateType.LC ? leftCurPoint : leftStartPoint.current;
            const endPoint = operateType === ResizeOperateType.RC ? rightCurPoint : rightStartPoint.current;

            const info = getBrushBaseInfo(identity, layer.data as IBrushLayerProps, startPoint, endPoint, {
                width: layer.width,
                height: layer.height,
            });

            const newLayer = cloneDeep(layer);
            const data = newLayer.data as IBrushLayerProps;
            if (data.brushType === BrushType.Line) {
                data.__drawPath = generateLinePath({ x: 0, y: 0 }, { x: info.width, y: 0 });
                data.naturalWidth = info.width;
                data.naturalHeight = 1;
                const res = getPathOffset(data.fills, data.stroke);
                newLayer.width = Math.max(data.naturalWidth + data.stroke.width + LAYER_EDGE_GAP, res.offsetWidth);
                newLayer.height = data.naturalHeight + res.offsetHeight;
                newLayer.left = Math.floor(info.left - (data.stroke.width + LAYER_EDGE_GAP));
                newLayer.top = Math.floor(info.top - res.translate);
                newLayer.rotate = info.rotate;
            } else if (data.brushType === BrushType.Arrow) {
                data.naturalWidth = info.naturalWidth;
                data.naturalHeight = info.naturalHeight;
                newLayer.width = ~~info.width;
                newLayer.height = ~~info.height;
                newLayer.left = ~~info.left;
                newLayer.top = ~~info.top;
                newLayer.rotate = info.rotate;
            } else if (data.brushType === BrushType.Wavy) {
                newLayer.width = ~~info.width;
                newLayer.height = ~~info.height;
                newLayer.left = ~~info.left;
                newLayer.top = ~~info.top;
                newLayer.rotate = info.rotate;
            }

            // 智能 45 90 度吸附
            const displayRotate = newLayer.rotate;
            const quotient = Math.round(displayRotate / 45);
            const remainder = displayRotate % 45;
            if (Math.abs(remainder) > 43 || Math.abs(remainder) < 2) {
                const fixAngle = quotient * 45 - displayRotate;
                const focusPoint = operateType === ResizeOperateType.LC ? endPoint : startPoint;
                const originCenter = getCenterPointForOrigin(newLayer);
                const newCenter = rotatePointWithFocus(originCenter, focusPoint, fixAngle);
                const distRotate = ~~(displayRotate + fixAngle);
                newLayer.rotate = distRotate;
                newLayer.top = newCenter.y - newLayer.height / 2;
                newLayer.left = newCenter.x - newLayer.width / 2;
            }

            framework.service.frame.addOrUpdateLayer(newLayer);
            framework.service.registration.getInstance<Displacement>('Displacement')?.set({
                type: 'resize',
                x: e.x,
                y: e.y,
                width: newLayer.width,
                height: newLayer.height,
                isMin: false,
                rotate: newLayer.rotate,
            });

            if (e.isEnd) {
                framework.service.registration.getInstance<Displacement>('Displacement')?.clear();
                framework.service.history.write(oldInfo.current, trackConfig[LayerType.BRUSH_LAYER].rotate);
                framework.service.editor.setIsBaseOperate(false);
                framework.service.frame.setActiveLayer(layer.id);
                updateScreenshotUrl(identity, layer.id);
            }
        },
    };
};
